import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";

const emptyBanner = {
  id: "",
  background_image: [],
  title: "",
  description: "",
  button_text: "",
  button_link: "",
  created_at: "",
  updated_at: "",
};

const BannerModal = ({
  showModal,
  selectedBanner,
  previewImage,
  handleChange,
  handleImageChange,
  handleSubmit,
  closeModal,
  submitting,
  message,
  error,
  modalMode,
}) => {
  if (!showModal || !selectedBanner) return null;

  return createPortal(
    <div style={styles.modalOverlay} onClick={closeModal}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.modalHeader}>
          <h3 style={styles.modalTitle}>
            {modalMode === "add" ? "Add New Banner" : "Edit Banner"}
          </h3>
          <button onClick={closeModal} style={styles.closeBtn} type="button">
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.imageBox}>
            <label style={styles.label}>Banner Image</label>

            {previewImage ? (
              <img
                src={previewImage}
                alt="Banner Preview"
                style={styles.image}
              />
            ) : (
              <div style={styles.noImageBox}>No image selected</div>
            )}

            <input
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              style={styles.input}
            />
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Title</label>
            <input
              type="text"
              name="title"
              value={selectedBanner.title || ""}
              onChange={handleChange}
              style={styles.input}
              placeholder="Enter banner title"
            />
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Description</label>
            <textarea
              name="description"
              value={selectedBanner.description || ""}
              onChange={handleChange}
              rows="4"
              style={styles.textarea}
              placeholder="Enter banner description"
            />
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Button Text</label>
            <input
              type="text"
              name="button_text"
              value={selectedBanner.button_text || ""}
              onChange={handleChange}
              style={styles.input}
              placeholder="Enter button text"
            />
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Button Link</label>
            <input
              type="text"
              name="button_link"
              value={selectedBanner.button_link || ""}
              onChange={handleChange}
              style={styles.input}
              placeholder="Enter button link"
            />
          </div>

          {message && <p style={styles.success}>{message}</p>}
          {error && <p style={styles.error}>{error}</p>}

          <button type="submit" style={styles.button} disabled={submitting}>
            {submitting
              ? "Saving..."
              : modalMode === "add"
              ? "Add Banner"
              : "Update Banner"}
          </button>
        </form>
      </div>
    </div>,
    document.body
  );
};

const BannerEditor = () => {
  const [banners, setBanners] = useState([]);
  const [selectedBanner, setSelectedBanner] = useState(null);
  const [previewImage, setPreviewImage] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("edit");

  useEffect(() => {
    fetchBanners();
  }, []);

  const fetchBanners = async () => {
    try {
      setLoading(true);
      setError("");

      const response = await fetch(
        "https://eventscons.com/eventbackendnew/api/banner-details"
      );

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const result = await response.json();

      if (result.status && result.data) {
        const bannerData = Array.isArray(result.data)
          ? result.data
          : [result.data];

        setBanners(bannerData);
      } else {
        throw new Error(result.message || "Failed to fetch banners");
      }
    } catch (err) {
      setError(err.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const openAddModal = () => {
    setModalMode("add");
    setSelectedBanner({ ...emptyBanner });
    setPreviewImage("");
    setMessage("");
    setError("");
    setShowModal(true);
  };

  const openEditModal = (banner) => {
    setModalMode("edit");
    setSelectedBanner({
      ...banner,
      background_image: banner.background_image || [],
    });
    setPreviewImage(banner.background_image?.[0] || "");
    setMessage("");
    setError("");
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedBanner(null);
    setPreviewImage("");
    setMessage("");
    setError("");
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setSelectedBanner((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];

    if (file) {
      const imageUrl = URL.createObjectURL(file);
      setPreviewImage(imageUrl);

      setSelectedBanner((prev) => ({
        ...prev,
        background_image: [file],
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setMessage("");
    setError("");

    try {
      const payload = new FormData();

      payload.append("title", selectedBanner.title || "");
      payload.append("description", selectedBanner.description || "");
      payload.append("button_text", selectedBanner.button_text || "");
      payload.append("button_link", selectedBanner.button_link || "");

      if (
        selectedBanner.background_image &&
        selectedBanner.background_image.length > 0 &&
        selectedBanner.background_image[0] instanceof File
      ) {
        payload.append("background_image[]", selectedBanner.background_image[0]);
      }

      let apiUrl = "https://eventscons.com/eventbackendnew/api/banner-add";

      if (modalMode === "edit") {
        apiUrl = "https://eventscons.com/eventbackendnew/api/banner-update";
        payload.append("id", selectedBanner.id);
      }

      const response = await fetch(apiUrl, {
        method: "POST",
        body: payload,
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(
          result.message ||
            (modalMode === "add"
              ? "Banner add failed"
              : "Banner update failed")
        );
      }

      setMessage(
        result.message ||
          (modalMode === "add"
            ? "Banner added successfully"
            : "Banner updated successfully")
      );

      await fetchBanners();

      setTimeout(() => {
        closeModal();
      }, 1000);
    } catch (err) {
      setError(err.message || "Something went wrong while saving");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    const isConfirmed = window.confirm(
      "Are you sure you want to delete this banner?"
    );

    if (!isConfirmed) return;

    try {
      setDeletingId(id);
      setMessage("");
      setError("");

      const response = await fetch(
        `https://eventscons.com/eventbackendnew/api/banner-delete/${id}`,
        {
          method: "DELETE",
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || "Banner delete failed");
      }

      setBanners((prev) => prev.filter((banner) => banner.id !== id));
      setMessage(result.message || "Banner deleted successfully");
    } catch (err) {
      setError(err.message || "Something went wrong while deleting");
    } finally {
      setDeletingId(null);
    }
  };

  if (loading) return <p style={styles.loading}>Loading banner details...</p>;
  if (error && banners.length === 0) return <p style={styles.error}>{error}</p>;

  return (
    <>
      <section style={styles.section}>
        <div style={styles.container}>
          <div style={styles.headerRow}>
            <h2 style={styles.heading}>Banner List</h2>

            <button
              type="button"
              style={styles.addButton}
              onClick={openAddModal}
            >
              + Add New Banner
            </button>
          </div>

          {message && <p style={styles.success}>{message}</p>}
          {error && <p style={styles.error}>{error}</p>}

          <div style={styles.grid}>
            {banners.map((banner) => (
              <div key={banner.id} style={styles.card}>
                <div style={styles.cardImageWrap}>
                  <img
                    src={banner.background_image?.[0] || ""}
                    alt={banner.title}
                    style={styles.cardImage}
                  />

                  <div style={styles.actionGroup}>
                    <button
                      style={styles.editBtn}
                      onClick={() => openEditModal(banner)}
                      type="button"
                      title="Edit Banner"
                    >
                      ✏️
                    </button>

                    <button
                      style={styles.deleteBtn}
                      onClick={() => handleDelete(banner.id)}
                      type="button"
                      title="Delete Banner"
                      disabled={deletingId === banner.id}
                    >
                      {deletingId === banner.id ? "..." : "🗑️"}
                    </button>
                  </div>
                </div>

                <h3 style={styles.cardTitle}>{banner.title}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      <BannerModal
        showModal={showModal}
        selectedBanner={selectedBanner}
        previewImage={previewImage}
        handleChange={handleChange}
        handleImageChange={handleImageChange}
        handleSubmit={handleSubmit}
        closeModal={closeModal}
        submitting={submitting}
        message={message}
        error={error}
        modalMode={modalMode}
      />
    </>
  );
};

const styles = {
  section: {
    padding: "40px 20px",
    border: "1px solid #eee",
    background: "#fff",
    margin: "30px",
    boxShadow: "0px 10px 25px #dcdcdc",
    borderRadius: "15px",
  },
  container: {
    maxWidth: "1200px",
    margin: "0 auto",
  },
  headerRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "16px",
    marginBottom: "25px",
    flexWrap: "wrap",
  },
  heading: {
    margin: 0,
    fontSize: "28px",
    fontWeight: "700",
    color: "#000",
    textAlign: "left",
  },
  cardTitle:{
    fontSize:'20px',
    padding:'20px',
  },
  addButton: {
    padding: "12px 18px",
    background: "#9B1321",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    fontSize: "15px",
    fontWeight: "600",
    cursor: "pointer",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
    gap: "20px",
  },
  card: {
    background: "#fff",
    borderRadius: "14px",
    overflow: "hidden",
    boxShadow: "0 4px 14px rgba(0,0,0,0.08)",
  },
  cardImageWrap: {
    position: "relative",
  },
  cardImage: {
    width: "100%",
    height: "220px",
    objectFit: "cover",
    display: "block",
  },
  actionGroup: {
    position: "absolute",
    top: "12px",
    right: "12px",
    display: "flex",
    gap: "8px",
  },
  editBtn: {
    width: "42px",
    height: "42px",
    borderRadius: "50%",
    border: "none",
    background: "#9B1321",
    color: "#fff",
    cursor: "pointer",
    fontSize: "18px",
    boxShadow: "0 4px 10px rgba(0,0,0,0.2)",
  },
  deleteBtn: {
    width: "42px",
    height: "42px",
    borderRadius: "50%",
    border: "none",
    background: "#dc3545",
    color: "#fff",
    cursor: "pointer",
    fontSize: "18px",
    boxShadow: "0 4px 10px rgba(0,0,0,0.2)",
  },
  modalOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100vw",
    height: "100vh",
    backgroundColor: "rgba(0,0,0,0.55)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "20px",
    zIndex: 99999,
  },
  modal: {
    position: "relative",
    width: "100%",
    maxWidth: "820px",
    maxHeight: "90vh",
    overflowY: "auto",
    background: "#fff",
    borderRadius: "16px",
    padding: "24px",
    boxShadow: "0 20px 50px rgba(0,0,0,0.25)",
  },
  modalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "20px",
  },
  modalTitle: {
    fontSize: "20px",
    fontWeight: "700",
    color: "#000",
    padding:"20px",
  },
  closeBtn: {
    background: "transparent",
    border: "none",
    fontSize: "30px",
    cursor: "pointer",
    color: "#444",
    lineHeight: 1,
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: "18px",
  },
  imageBox: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
  },
  image: {
    width: "100%",
    maxWidth: "100%",
    height: "260px",
    objectFit: "cover",
    borderRadius: "10px",
    border: "1px solid #ddd",
  },
  noImageBox: {
    height: "180px",
    border: "1px dashed #bbb",
    borderRadius: "10px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#666",
    background: "#fafafa",
    fontWeight: "500",
  },
  field: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  label: {
    fontWeight: "600",
    color: "#333",
  },
  input: {
    padding: "12px 14px",
    borderRadius: "8px",
    border: "1px solid #ccc",
    fontSize: "15px",
    width: "100%",
  },
  textarea: {
    padding: "12px 14px",
    borderRadius: "8px",
    border: "1px solid #ccc",
    fontSize: "15px",
    resize: "vertical",
    width: "100%",
  },
  button: {
    padding: "14px 20px",
    background: "#9B1321",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    fontSize: "16px",
    fontWeight: "600",
    cursor: "pointer",
    width: "fit-content",
  },
  success: {
    color: "green",
    fontWeight: "600",
    marginBottom: "16px",
  },
  error: {
    color: "red",
    fontWeight: "600",
    marginBottom: "16px",
  },
  loading: {
    padding: "40px",
    textAlign: "center",
  },
};

export default BannerEditor;